import clsx from "clsx";
import { BookOpen, Loader2 } from "lucide-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { Progress } from "~/components/ui/progress";
import { useSupportiveMaterialsPrompt } from "~/features/cases/hooks/useSupportiveMaterialsPrompt";
import { MAX_FILE_SIZE_BYTES } from "~/features/settings/components/reports/importExportUtils";
import { useKnowledgeDocumentUpload } from "~/features/settings/hooks/useKnowledgeDocumentUpload";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { trackEvent } from "~/utils/analytics";

const ACCEPTED_SUPPORTIVE_MATERIAL_TYPES = {
  "application/pdf": [".pdf"],
  "text/markdown": [".md", ".markdown"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "application/msword": [".doc"],
};

/**
 * One-time prompt surfaced on the user's second non-demo case (see
 * `useSupportiveMaterialsPrompt`). It invites the user to share reference
 * materials so Sky AI can tailor reports to how they work.
 *
 * "Skip for Now" closes it for now; uploading materials or ticking
 * "Hide this" suppresses it permanently on this device.
 */
export const SupportiveMaterialsModal: FC = () => {
  const intl = useIntl();
  const { toast } = useToast();
  const { organizationId } = useOrganizationParams();
  const { shouldShow, hidePermanently } = useSupportiveMaterialsPrompt();
  const { uploadKnowledgeDocuments, isUploading, uploadProgress } =
    useKnowledgeDocumentUpload({ organizationId });

  const [isOpen, setIsOpen] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [hideForever, setHideForever] = useState(false);
  // Keyed by organizationId: React Router reuses this component instance across
  // org switches (only the route param changes), so a single boolean would
  // suppress the prompt for every other org after it fires once.
  const hasShownByOrgRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (shouldShow && !hasShownByOrgRef.current[organizationId]) {
      hasShownByOrgRef.current[organizationId] = true;
      // Start each org's prompt from a clean slate. The instance is reused
      // across org switches, so without this a "Hide this" tick (or active
      // drag) from a previous org would carry over to the next one.
      setHideForever(false);
      setIsDragActive(false);
      setIsOpen(true);
      trackEvent({
        eventName: "SupportiveMaterials_Prompt_Shown",
        properties: { organizationId },
      });
    }
  }, [shouldShow, organizationId]);

  const isBusy = isUploading;

  // Closing the modal without uploading. If the user ticked "Hide this", the
  // prompt is suppressed permanently on this device; otherwise it's a soft
  // skip that may reappear until they add materials or move past two cases.
  const dismiss = useCallback(() => {
    if (hideForever) {
      hidePermanently();
      trackEvent({
        eventName: "SupportiveMaterials_Prompt_Hidden",
        properties: { organizationId },
      });
    } else {
      trackEvent({
        eventName: "SupportiveMaterials_Prompt_Skipped",
        properties: { organizationId },
      });
    }
    setIsOpen(false);
  }, [hideForever, hidePermanently, organizationId]);

  const handleUpload = useCallback(
    async (files: File[]) => {
      try {
        await uploadKnowledgeDocuments(files);
        trackEvent({
          eventName: "SupportiveMaterials_Uploaded",
          properties: { organizationId, fileCount: files.length },
        });
        toast({
          title: intl.formatMessage({
            id: "ftue.supportiveMaterials.successTitle",
            defaultMessage: "Materials added",
          }),
          description: intl.formatMessage({
            id: "ftue.supportiveMaterials.successSubtitle",
            defaultMessage:
              "Sky AI will use these to tailor reports to how you work.",
          }),
        });
        setIsOpen(false);
      } catch (error) {
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "ftue.supportiveMaterials.errorTitle",
            defaultMessage: "Failed to upload materials",
          }),
          description:
            error instanceof Error
              ? error.message
              : intl.formatMessage({
                  id: "ftue.supportiveMaterials.errorSubtitle",
                  defaultMessage: "Something went wrong. Please try again.",
                }),
        });
      }
    },
    [uploadKnowledgeDocuments, organizationId, toast, intl]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDrop: (acceptedFiles, fileRejections) => {
      setIsDragActive(false);
      if (fileRejections.length > 0) {
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "ftue.supportiveMaterials.invalidFile",
            defaultMessage: "Only PDF, Word, and Markdown files are allowed",
          }),
        });
        return;
      }
      const oversized = acceptedFiles.find((f) => f.size > MAX_FILE_SIZE_BYTES);
      if (oversized) {
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "importTemplate.fileTooLarge",
            defaultMessage: "File size exceeds the maximum limit of 25MB",
          }),
        });
        return;
      }
      if (acceptedFiles.length > 0) {
        void handleUpload(acceptedFiles);
      }
    },
    accept: ACCEPTED_SUPPORTIVE_MATERIAL_TYPES,
    multiple: true,
    disabled: isBusy,
  });

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isBusy) {
          dismiss();
        }
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="ftue.supportiveMaterials.title"
              defaultMessage="Make Sky AI Work the Way You Do"
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="ftue.supportiveMaterials.subtitle"
              defaultMessage="Share any supportive materials — internal guidelines, references, or existing workflows. Sky AI uses these to tailor reports to how you work."
            />
          </DialogDescription>
        </DialogHeader>

        <div
          {...getRootProps()}
          className={clsx(
            "rounded-lg border border-dashed transition-all cursor-pointer",
            {
              "border-purple-300 bg-purple-50/50 dark:border-purple-700 dark:bg-purple-950/50":
                isUploading,
              "border-purple-500 shadow-md bg-purple-50/30 dark:bg-purple-950/30":
                isDragActive && !isUploading,
              "hover:border-purple-500 hover:shadow-sm":
                !isBusy && !isDragActive,
            }
          )}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center text-center py-10 px-6">
            {isUploading ? (
              <>
                <div className="mb-4 w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
                </div>
                <p className="text-base font-medium text-purple-700 dark:text-purple-300">
                  <FormattedMessage
                    id="ftue.supportiveMaterials.uploading"
                    defaultMessage="Uploading your materials..."
                  />
                </p>
                <div className="mt-4 w-full max-w-xs">
                  <Progress value={uploadProgress} className="h-2" />
                  <p className="text-sm text-muted-foreground mt-2">
                    {uploadProgress}%
                  </p>
                </div>
              </>
            ) : (
              <>
                <div
                  className={clsx(
                    "mb-4 w-14 h-14 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center",
                    { "opacity-50": isDragActive }
                  )}
                >
                  <BookOpen className="w-7 h-7 text-purple-600" />
                </div>
                <p className="text-base font-medium text-foreground">
                  <FormattedMessage
                    id="ftue.supportiveMaterials.dropzone"
                    defaultMessage="Click to upload supportive materials"
                  />
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  <FormattedMessage
                    id="ftue.supportiveMaterials.formats"
                    defaultMessage="Supports PDF, Word, and Markdown files (max 25MB)"
                  />
                </p>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-center gap-2">
          <Checkbox
            id="hide-supportive-materials"
            checked={hideForever}
            onCheckedChange={(checked) => setHideForever(checked === true)}
            disabled={isBusy}
          />
          <Label
            htmlFor="hide-supportive-materials"
            className="text-sm font-normal text-muted-foreground cursor-pointer"
          >
            <FormattedMessage
              id="ftue.supportiveMaterials.hide"
              defaultMessage="Hide this, I'll do this later in Settings > Knowledge"
            />
          </Label>
        </div>

        <DialogFooter className="sm:justify-center">
          <Button
            variant="link"
            onClick={dismiss}
            disabled={isBusy}
            className="text-muted-foreground hover:text-foreground"
          >
            <FormattedMessage
              id="ftue.supportiveMaterials.skip"
              defaultMessage="Skip for Now"
            />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
